// src/components/CustomerData.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import "../Details.css";
import "./CustomerData.css";
import { Link, useParams, useNavigate } from "react-router-dom";
import BarChart from "./BarChart";
import { useAuth } from "../../auth/AuthContext";
import { openPrintWindow, buildFiltersSummaryHtml } from "./printChartTemplate";
import { busyErrorFrom, useDataApiBusy } from "../../services/dataApiBusy";
import BusyNotice from "./BusyNotice";

function CustomerData({ periodSelection }) {
  const pageTitle = "CustomerData";

  const {
    user,
    tenantServerUrl,
    primaryRestaurantId,selectedRestaurantId,
    accessToken,
  } = useAuth();

  // Normalize token to "Bearer <token>" and avoid double Bearer
  const bearerToken = useMemo(() => {
    if (!accessToken) return null;
    return accessToken.startsWith("Bearer ")
      ? accessToken
      : `Bearer ${accessToken}`;
  }, [accessToken]);

  const {
    questionCategoryID: categoryIDParam,
    questionID: questionIDParam,
  } = useParams();
  const navigate = useNavigate();

  const dataCategoryID = "6";
  const categoryID = categoryIDParam;
  const questionID = questionIDParam || "1";

  const RestaurantID = selectedRestaurantId 

  console.log(
    `Route -> dataCategoryID: ${dataCategoryID}, categoryID: ${categoryID}, questionID: ${questionIDParam}`
  );
  console.log("[CustomerData] tenantServerUrl:", tenantServerUrl);
  console.log("[CustomerData] RestaurantID:", RestaurantID);
  console.log("[CustomerData] bearerToken present:", !!bearerToken);

  const [questionsList, setQuestionsList] = useState([]);
  const [questionTypesMap, setQuestionTypesMap] = useState({});
  const [chartProperties, setChartProperties] = useState(null);
  const [wordCloudHtml, setWordCloudHtml] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [isQuestionDataLoading, setIsQuestionDataLoading] = useState(false);
  const [questionTitle, setQuestionTitle] = useState("Question: ");
  const [questionNumber, setQuestionNumber] = useState("0");
  const [questionDataMap, setQuestionDataMap] = useState({});
  const [chartType, setChartType] = useState(() => {
    return localStorage.getItem("chartType") || "line";
  });
  const [showFilterInfo, setShowFilterInfo] = useState(false);
  const wordCloudPrintRef = useRef(null);

  const REDIR_BASE = "https://mydata.tablemate.net/redir.php?url=";
  const ANALYSE_BASE_URL = "https://data.tablemate.net/analyse";

  const [employeeFilterNames, setEmployeeFilterNames] = useState([]);
  const employeeFilterCsv = useMemo(
    () =>
      employeeFilterNames && employeeFilterNames.length
        ? employeeFilterNames.join(",")
        : "",
    [employeeFilterNames]
  );

  // Normalize route: /data/6/:categoryID -> /data/6/:categoryID/1
  useEffect(() => {
    if (categoryID && !questionIDParam) {
      navigate(`/data/${dataCategoryID}/${categoryID}/1`, { replace: true });
    }
  }, [categoryID, questionIDParam, dataCategoryID, navigate]);

  // Build tenant-specific questions endpoint
  const QUESTIONS_API_URL = useMemo(() => {
    if (!tenantServerUrl) return null;
    return `${tenantServerUrl.replace(
      /\/$/,
      ""
    )}/tablemate/endpoints/statistics/fetchQuestionsBESA.php`;
  }, [tenantServerUrl]);

  // Fetch questions for this category
  useEffect(() => {
    if (!categoryID || !QUESTIONS_API_URL || !bearerToken) {
      if (!bearerToken) {
        console.log("[CustomerData] Skipping questions fetch: no bearerToken yet.");
      }
      return;
    }

    const controller = new AbortController();

    async function loadQuestions() {
      try {
        const proxiedUrl = `${REDIR_BASE}${encodeURIComponent(
          QUESTIONS_API_URL
        )}&categoryID=${encodeURIComponent(categoryID)}`;

        console.log("category:", categoryID);
        console.log("Downloading questions:", proxiedUrl);

        const res = await fetch(proxiedUrl, {
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: bearerToken,
          },
        });

        if (!res.ok) {
          const text = await res.text();
          console.error("Failed to load questions:", res.status, text);
          return;
        }

        const data = await res.json();
        console.log("Question list result:", data);
        setQuestionsList(data || []);

        const tMap = {};
        const dMap = {};
        (data || []).forEach((q) => {
          const qID = q.questionID ?? q.id;
          if (qID != null) {
            dMap[String(qID)] = q;
            if (q.type) {
              tMap[String(qID)] = q.type;
            }
          }
        });
        setQuestionTypesMap(tMap);
        setQuestionDataMap(dMap);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading questions:", err);
        }
      }
    }

    loadQuestions();
    return () => controller.abort();
  }, [categoryID, QUESTIONS_API_URL, REDIR_BASE, bearerToken]);

  const activateQuestion = (q, replace = false) => {
    const qID = q.questionID ?? q.id;
    setQuestionNumber(q.question_number);
    setChartType("bar");
    localStorage.setItem("chartType", "bar");

    navigate(`/data/${dataCategoryID}/${categoryID}/${qID}`, {
      replace,
    });
  };

  // Auto-activate first question if route question invalid/missing
  useEffect(() => {
    if (!categoryID || !questionsList.length) return;

    const ids = questionsList.map((q) => String(q.questionID ?? q.id));
    const currentID = String(questionID);
    const hasCurrent = ids.includes(currentID);

    if (!hasCurrent) {
      const first = questionsList[0];
      if (first) {
        activateQuestion(first, true);
      }
    } else if (!questionNumber || questionNumber === "0") {
      const current = questionsList.find(
        (q) => String(q.questionID ?? q.id) === currentID
      );
      if (current) {
        setQuestionNumber(current.question_number);
      }
    }
  }, [
    questionsList,
    categoryID,
    questionID,
    questionNumber,
    dataCategoryID,
  ]);

  // Derive questionType
  useEffect(() => {
    if (!questionID) return;
    const typeFromMap = questionTypesMap[String(questionID)];
    if (typeFromMap) {
      setQuestionType(typeFromMap);
      console.log("Derived question type:", typeFromMap);
    } else {
      setQuestionType("rating");
    }
  }, [questionID, questionTypesMap]);

  // Date utilities
  const formatYMD = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}`;
  };
  const formatYM = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  };

  const resolvedPeriod = periodSelection || {
    mode: "today",
    start: null,
    end: null,
  };
  const mode = resolvedPeriod.mode;
  const start = resolvedPeriod.start;
  const end = resolvedPeriod.end;

  const {
    chartPeriod,
    chartDateSelected,
    fromParam,
    toParam,
    periodLabel,
  } = useMemo(() => {
    console.log("CustomerData: recomputing period from", {
      mode,
      start,
      end,
    });

    const fmtShort = (d) =>
      d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });

    let p = "3";
    let dVal = "";
    let from = "";
    let to = "";
    let label = "Today";

    if (!start || !end) {
      return {
        chartPeriod: p,
        chartDateSelected: dVal,
        fromParam: from,
        toParam: to,
        periodLabel: label,
      };
    }

    from = formatYMD(start);
    to = formatYMD(end);

    switch (mode) {
      case "month": {
        p = "1";
        dVal = formatYM(start);
        const now = new Date();
        if (
          start.getFullYear() === now.getFullYear() &&
          start.getMonth() === now.getMonth()
        ) {
          label = "This Month";
        } else {
          label = start.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
          });
        }
        break;
      }
      case "year": {
        p = "2";
        dVal = String(start.getFullYear());
        const now = new Date();
        if (start.getFullYear() === now.getFullYear()) {
          label = `This Year (${start.getFullYear()})`;
        } else {
          label = String(start.getFullYear());
        }
        break;
      }
      case "today": {
        // Explicit local date so analyse API matches client calendar day
        const todayLocal = start || new Date();
        const formatted = formatYMD(todayLocal);
        p = "5";
        dVal = formatted;
        from = formatted;
        to = formatted;
        label = `Today - ${formatted}`;
        break;
      }
      case "range":
        p = "4";
        label = `${fmtShort(start)} – ${fmtShort(end)}`;
        break;
      case "date":
        p = "5";
        dVal = from;
        label = fmtShort(start);
        break;
      default:
        p = "0";
        label = "All Time";
        break;
    }

    return {
      chartPeriod: p,
      chartDateSelected: dVal,
      fromParam: from,
      toParam: to,
      periodLabel: label,
    };
  }, [mode, start, end]);

  const questionRequestKey = [categoryID, questionID, questionType, chartPeriod, chartDateSelected, fromParam, toParam, employeeFilterCsv].join("|");
  const {
    busy: questionBusy,
    setBusy: setQuestionBusy,
    attempt: questionBusyAttempt,
    retry: retryQuestion,
  } = useDataApiBusy(questionRequestKey);

  // Fetch chart/wordcloud data for question
  useEffect(() => {
    if (!categoryID || !questionID || !questionType || !bearerToken) {
      if (!bearerToken) {
        console.log("[CustomerData] Skipping chart fetch: no bearerToken yet.");
      }
      return;
    }

    const controller = new AbortController();

    async function loadChartForQuestion() {
      try {
        setIsQuestionDataLoading(true);
        setWordCloudHtml("");

        const analyseUrl =
          `${ANALYSE_BASE_URL}` +
          `?rid=${encodeURIComponent(RestaurantID)}` +
          `&cid=6` +
          `&chid=${encodeURIComponent(categoryID)}` +
          `&p=${encodeURIComponent(chartPeriod || "3")}` +
          `&f=${encodeURIComponent(employeeFilterCsv || "")}` +
          `&d=${encodeURIComponent(chartDateSelected || "")}` +
          `&from=${encodeURIComponent(fromParam || "")}` +
          `&to=${encodeURIComponent(toParam || "")}` +
          `&menf=` +
          `&t=${encodeURIComponent(chartType || "")}` +
          `&qID=${encodeURIComponent(questionID)}` +
          `&exp=${questionType === "comment" ? "" : "raw"}` +
          `&qt=${encodeURIComponent(questionType).replaceAll(
            "rating",
            "radio"
          )}`;

        const proxiedUrl = `${REDIR_BASE}${encodeURIComponent(analyseUrl)}`;
        console.log("Cust URL " + analyseUrl);
        console.log("Downloading chart for question:", proxiedUrl);

        const res = await fetch(proxiedUrl, {
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: bearerToken,
          },
        });

        if (!res.ok) {
          const busyErr = await busyErrorFrom(res);
          if (busyErr) {
            console.error("Chart data turned away (server busy):", busyErr.message);
            setQuestionBusy(busyErr);
            return;
          }
          const text = await res.text();
          console.error("Failed to load chart data:", res.status, text);
          return;
        }
        setQuestionBusy(null);

        const qMeta = questionDataMap[String(questionID)] || {};
        const questionTitleDisplay = qMeta.title || "";
        const questionStatus =
          qMeta.active == "1" || qMeta.active == "2"
            ? "Active"
            : "Inactive";

        let questionTypeDisplay = "";
        switch (questionType) {
          case "comment":
            questionTypeDisplay = "Comment";
            break;
          case "yes":
            questionTypeDisplay = "Yes/No";
            break;
          case "radio":
          case "rating":
          default:
            questionTypeDisplay = "Rating";
            break;
        }

        setQuestionTitle(
          `ID: ${questionID} - ${questionTitleDisplay} - App Question Number: ${questionNumber} [${questionStatus}] - Type: ${questionTypeDisplay} (Period: ${periodLabel})`
        );

        if (questionType === "comment") {
          const html = await res.text();
          console.log("Word cloud HTML length:", html.length);
          const strippedHtml = stripCssFromHtml(html);
          setWordCloudHtml(strippedHtml);
          setChartProperties(null);
        } else {
          const raw = await res.json();
          console.log("Raw chart data result:", raw);

          const rawOptions =
            raw && typeof raw === "object" && !Array.isArray(raw)
              ? raw.options || {}
              : {};

          const rawSeries = Array.isArray(raw?.series)
            ? raw.series
            : Array.isArray(raw)
            ? raw
            : [];

          const baseType =
            rawOptions?.chart?.type || chartType || "bar";

          const safeOptions = {
            ...rawOptions,
            chart: {
              type: baseType,
              ...(rawOptions.chart || {}),
            },
            xaxis: {
              ...(rawOptions.xaxis || {}),
              categories: rawOptions.xaxis?.categories || [],
            },
          };

          const safeChartProps = {
            options: safeOptions,
            series: rawSeries,
          };

          console.log("Normalized chartProperties:", safeChartProps);

          setChartProperties(safeChartProps);
          setWordCloudHtml("");

          const serverType = safeOptions.chart?.type;
          if (serverType) {
            setChartType(serverType);
            localStorage.setItem("chartType", serverType);
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading chart data:", err);
        }
      } finally {
        setIsQuestionDataLoading(false);
      }
    }

    loadChartForQuestion();
    return () => controller.abort();
  }, [
    categoryID,
    questionID,
    questionType,
    REDIR_BASE,
    ANALYSE_BASE_URL,
    chartType,
    questionDataMap,
    questionNumber,
    chartPeriod,
    chartDateSelected,
    fromParam,
    toParam,
    periodLabel,
    employeeFilterCsv,
    RestaurantID,
    bearerToken,
    questionBusyAttempt,
    setQuestionBusy,
  ]);

  const handleQuestionClick = (q) => {
    activateQuestion(q, false);
  };

  const cleanedProps = cleanChartProperties(chartProperties, questionType);
// Above your component return (or outside the component if you prefer)
function getRatingCategoryLabel(categoryID) {
  // Make sure we’re working with a number (params come in as strings)
  const id = Number(categoryID);

  switch (id) {
    case 0:
      return "Host Introduction Feedback";
    case 1:
      return "Food Feedback";
    case 2:
      return "Establishment Atmosphere Feedback";
    case 3:
      return "Service Feedback";
    case 4:
      return "Table Mate Feedback";
    default:
      return "All Feedback";
  }
}

  const handlePrintWordCloud = () => {
    if (!wordCloudPrintRef.current) return;

    const summaryItems = [
      `<li><strong>Restaurant:</strong> ${RestaurantID}</li>`,
      `<li><strong>Category (cid):</strong> ${categoryID || "1"}</li>`,
      `<li><strong>Question (qID):</strong> ${questionID}</li>`,
      `<li><strong>Period Label:</strong> ${periodLabel}</li>`,
      `<li><strong>Period Params:</strong> p=${chartPeriod}, d=${chartDateSelected || '""'}, from=${fromParam || '""'}, to=${toParam || '""'}</li>`,
      `<li><strong>Employees (f):</strong> ${employeeFilterNames?.length ? employeeFilterNames.join(", ") : "All"}</li>`,
    ];

    openPrintWindow({
      title: "Customer Comments - Word Cloud",
      contentHtml: wordCloudPrintRef.current.innerHTML,
      summaryHtml: buildFiltersSummaryHtml(summaryItems),
      user,
      selectedRestaurantId,
    });
  };

  return (
    <div className="details-container-page">
      <center>
        <h3 className="rating-category-title">Rating Category: {getRatingCategoryLabel(categoryID)}</h3>
        <br />
      </center>

      <div className="row">
        {/* LEFT: Question list */}
        <div className="col-12 col-lg-3">
          <h4 className="select-a-question-title">Select a Question:</h4>
          <div id="rating-quesionslist">
            {questionsList && questionsList.length > 0 ? (
              questionsList.map((q) => {
                const qID = q.questionID ?? q.id;
                const isActive = String(qID) === String(questionID);
               const statusActive = q.active == "1" || q.active == "2";   
                let qType = "Rating";
                switch (q.type) {
                  case "comment":
                    qType = "Comment";
                    break;
                  case "yes":
                    qType = "Yes/No";
                    break;
                  default:
                    qType = "Rating";
                    break;
                }

                return (
                  <div
                      key={q.id ?? qID}
                      className={`tm-question-row ${isActive ? "tm-question-row-selected" : ""}`}
                      onClick={() => handleQuestionClick(q)}   // ← keep original behavior
                    >
                      {/* Left icon / number */}
                      <div className="tm-q-leading">
                        <div className="tm-q-circle">
                          {q.id}
                        </div>
                      </div>

                      {/* Main content */}
                      <div className="tm-q-content">
                        <div className="tm-q-title-row">
                          <span className="tm-q-title">
                            {q.title || "Untitled question"}
                          </span>

                        
                        </div>

                    {/* Show question type */}
                        <div className="tm-q-type">
                        <span className="tm-q-type-pill">
                            {qType === "Rating" && "⭐ Rating"}
                            {qType === "Comment" && "💬 Comment"}
                            {qType === "Yes/No" && "✔ Yes / No"}
                            {!["Rating", "Comment", "Yes/No"].includes(qType) && qType}
                          </span>
                        </div>
                        {/* Show the full question preview */}
                        <div className="tm-q-preview">
                          {q.question || ""}
                        </div>

                        <div className="tm-q-meta-row">
                          <span
                            className={`tm-q-status-dot ${
                              statusActive ? "tm-q-status-dot-active" : "tm-q-status-dot-inactive"
                            }`}
                          />
                          <span className="tm-q-status-label">
                            {statusActive ? "Active" : "Inactive"}
                          </span>
                          <span className="tm-q-id-label">Question #{questionNumber}</span>
                        </div>
                      </div>
                    </div>

                );
              })
            ) : (
              <p>No questions found for this category.</p>
            )}
          </div>
        </div>

        {/* RIGHT: Chart / Word cloud for selected question */}
        <div className="col-12 col-lg-9">
          <center>
            <h5 className="rating-title">{questionTitle}</h5>
          </center>
          <div id="rating-quesions-data">
            {isQuestionDataLoading ? (
              <div className="chart-loader">
                <div className="chart-loader-spinner" />
                <span>Loading question data...</span>
              </div>
            ) : questionBusy ? (
              <BusyNotice message={questionBusy.message} onRetry={retryQuestion} minHeight={220} />
            ) : questionType === "comment" ? (
              wordCloudHtml ? (
                <div ref={wordCloudPrintRef}>
                  <div className="chart-header-row wordcloud-chart-header">
                    <div>
                      <p className="chart-period">Period: {periodLabel}</p>
                    </div>
                    <div className="chart-meta-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setShowFilterInfo((v) => !v)}
                      >
                        {showFilterInfo
                          ? "Hide Filters & Parameters"
                          : "View Filters & Parameters"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary ms-2"
                        onClick={handlePrintWordCloud}
                      >
                        Print Chart
                      </button>
                    </div>
                  </div>
                  {showFilterInfo && (
                    <div className="chart-filters-summary">
                      <h5>Applied Filters & Parameters</h5>
                      <ul>
                        <li><strong>Restaurant:</strong> {RestaurantID}</li>
                        <li><strong>Category (cid):</strong> {categoryID || "1"}</li>
                        <li><strong>Question (qID):</strong> {questionID}</li>
                        <li><strong>Period Label:</strong> {periodLabel}</li>
                        <li><strong>Period Params:</strong>{" "}
                          {`p=${chartPeriod}, d=${chartDateSelected || '""'}, from=${fromParam || '""'}, to=${toParam || '""'}`}
                        </li>
                        <li><strong>Employees (f):</strong>{" "}
                          {employeeFilterNames?.length ? employeeFilterNames.join(", ") : "All"}
                        </li>
                      </ul>
                    </div>
                  )}
                  <div
                    className="wordcloud-container"
                    dangerouslySetInnerHTML={{ __html: wordCloudHtml }}
                  />
                </div>
              ) : (
                <p>Loading word cloud and sentiment data...</p>
              )
            ) : chartProperties ? (
              <BarChart
                title={`Ratings for Question ${questionID} - ${questionTitle} 
                  ${questionsList.find(
                    (q) => String(q.questionID ?? q.id) === String(questionID)
                  )?.question ?? questionTitle
                }`}
                chartProperties={cleanedProps}
                height={500}
                periodSelection={periodSelection}
                hasAdvancedOptions={true}
                hideSecondCategory={true}
                defaultEmployeeFilter={employeeFilterNames}
                onEmployeeFilterChange={setEmployeeFilterNames}
              />
            ) : (
              <p>Loading chart...</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerData;

// Strip CSS from HTML
function stripCssFromHtml(html) {
  if (!html) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  doc.querySelectorAll("style, link[rel='stylesheet']").forEach((el) =>
    el.remove()
  );

  doc.querySelectorAll("[style]").forEach((el) =>
    el.removeAttribute("style")
  );

  return doc.body.innerHTML;
}

// Clean chart properties based on questionType
export function cleanChartProperties(chartProperties, questionType) {
  const cleaned = JSON.parse(JSON.stringify(chartProperties || {}));

  if (!cleaned.options) cleaned.options = {};
  if (!cleaned.options.chart) cleaned.options.chart = {};
  if (!cleaned.options.xaxis) cleaned.options.xaxis = {};
  if (!Array.isArray(cleaned.series)) cleaned.series = [];

  cleaned.options.chart.type = cleaned.options.chart.type || "bar";

  if (questionType === "yes") {
    cleaned.options.xaxis.categories = ["Yes", "No"];
  } else if (questionType === "radio" || questionType === "rating") {
    cleaned.options.xaxis.categories = [
      "1 Star",
      "2 Stars",
      "3 Stars",
      "4 Stars",
      "5 Stars",
    ];
  } else {
    cleaned.options.xaxis.categories =
      cleaned.options.xaxis.categories || [];
  }

  const catCount = cleaned.options.xaxis.categories.length;
  if (catCount > 0) {
    cleaned.series = cleaned.series.map((s) => ({
      ...s,
      data: Array.isArray(s.data) ? s.data.slice(0, catCount) : [],
    }));
  }

  return cleaned;
}
